/**
 * PriorityValuesCards — the physical "Priority Values" deck that ships with
 * ValuesIncubator+.
 *
 * WHAT THIS SHOWS
 * ---------------
 * The product page used to be entirely software: screenshots of the student
 * app, the teacher console, the dashboards. Nothing told a head of school what
 * the VALUE side of the programme physically looks like. This section puts the
 * printed deck on the page — the twelve cards fanned out like a hand of cards,
 * the printable sheet, the material specs and the classroom prompts.
 *
 * THE FAN
 * -------
 * The fan is a real arc, not a row of tilted rectangles. Every card's centre
 * sits at `radius × sin/cos` of its own angle around a pivot below the stage,
 * and the card is rotated by that angle — so the deck reads as cards in a
 * hand. The geometry is expressed in multiples of the card width (see
 * `.pv-fan` / `.pv-card` in src/styles/globals.css), which means the whole
 * fan scales with the viewport without a single line of JS measuring it.
 * Only the per-card angles live in JS, and they are static.
 *
 * Hovering (or focusing) a card lifts it out of the fan; clicking opens the
 * enlarged view with that value's classroom prompt. On a phone the fan is
 * horizontally scrollable and the same lift/click behaviour applies.
 *
 * THE ARTWORK
 * ------------
 * The card images are the real printed artwork, converted to WebP and served
 * as static assets (`public/products/priority-values/`). They are NOT in
 * `cms_media`: the deck is a manufactured product with one fixed design, not
 * per-school content.
 *
 * THE DATA
 * --------
 * This component takes its data as a prop and knows nothing about where it
 * came from. The page hands it the printed deck (src/content/priorityValues.ts)
 * with the CMS's own copy merged over it — the Chinese name, the classroom
 * prompt and the module each card speaks for. The default prop is the
 * printed deck itself, so the component still renders on its own.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as RadixDialog from '@radix-ui/react-dialog'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { Icon } from '@iconify/react'
import { Container, Reveal } from '@/components/ui'
import {
  PRIORITY_VALUES,
  PRIORITY_VALUES_PDF,
  PRIORITY_VALUES_SHEET,
  inkFor,
  localisePriorityValues,
  type PriorityValue,
} from '@/content/priorityValues'
import CardZoomViewer from './CardZoomViewer'

/**
 * Half-angle of the fan, in degrees — the tilt of the outermost card.
 *
 * WHY 55° AND NOT 42°
 * -------------------
 * Every card carries its printed title across the TOP of the artwork, and
 * the card on the right overlaps the one on its left (z-index grows to the
 * right, so the right-hand neighbour is the one that covers). With the old
 * geometry the neighbour's edge cut the title at 79% of the card width:
 * "NATIONAL IDENTITY" read as "NATIONAL IDENT".
 *
 * The overlap has to slide BELOW the title band. For cards that rotate about
 * their own centres on a circle of radius R and are spaced Δ apart, the
 * neighbour's left edge crosses this card's top edge at
 *
 *     x = (R + 0.5)·tan Δ − 0.5·sec Δ      (in card widths)
 *
 * Setting x to 1 — the corner — makes two neighbouring top corners just
 * touch, so the overlap falls away down into the artwork and the printed
 * title stays whole:
 *
 *     R = 0.5·cot(Δ/2) − 0.5
 *
 * Twelve cards spread over ±55° are Δ = 10° apart, which wants R = 5.18;
 * 5.28 adds a couple of pixels of clearance so the widest printed title
 * ("RESPECT FOR OTHERS", which runs to 96% of the card width) never
 * grazes its neighbour's edge. The deck still reads as a hand of cards —
 * the neighbours overlap by tan Δ ≈ 17% of a card at the bottom — but
 * nothing covers a title.
 */
const FAN_ANGLE = 55
/** Pivot distance as a multiple of the card width (see `.pv-fan` in CSS). */
const FAN_RADIUS = 5.28

/** The geometry of one card in the fan, all in % of the card's own size. */
function fanTransform(index: number, total: number): { dx: number; dy: number; angle: number } {
  const t = total === 1 ? 0 : (index / (total - 1)) * 2 - 1 // -1 … 1
  const rad = (t * FAN_ANGLE * Math.PI) / 180
  return {
    dx: FAN_RADIUS * 100 * Math.sin(rad),
    dy: FAN_RADIUS * 100 * (1 - Math.cos(rad)),
    angle: t * FAN_ANGLE,
  }
}

export default function PriorityValuesCards({
  values = PRIORITY_VALUES,
}: {
  values?: PriorityValue[]
}) {
  const { t, i18n } = useTranslation()
  const reduced = useReducedMotion()
  const cards = useMemo(
    () => localisePriorityValues(values, i18n.language),
    [values, i18n.language],
  )

  const [openIndex, setOpenIndex] = useState<number | null>(null)
  /**
   * Whether the zoomed view is showing over the lightbox.
   *
   * The lightbox shows the card at `min(78vw, 22rem)` — enough to read
   * the value's name, not enough to read the printed slogan on the card.
   * The zoom view takes the artwork to 1:1, where the 1024px file has the
   * pixels to actually be read.
   */
  const [zoomed, setZoomed] = useState(false)
  /**
   * The card the colour-code legend is pointing at.
   *
   * The legend (twelve numbered chips, one per card) is a second way into
   * the deck: hovering a chip lifts the card it names, clicking opens it.
   * `null` means nothing is being pointed at.
   */
  const [hoverIndex, setHoverIndex] = useState<number | null>(null)
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])

  /**
   * The card that opened the lightbox.
   *
   * Radix restores focus to its own `Dialog.Trigger` on close, and this
   * lightbox has none — the trigger is a plain button in the fan. Without
   * this the reader's focus would fall back to `<body>` after closing.
   */
  const triggerRef = useRef<HTMLButtonElement | null>(null)

  const openCard = useCallback((index: number, el: HTMLButtonElement | null) => {
    triggerRef.current = el
    setOpenIndex(index)
  }, [])

  const total = cards.length
  const active = openIndex ?? 0

  const goPrev = useCallback(() => {
    if (!total) return
    setOpenIndex((i) => ((i ?? 0) - 1 + total) % total)
  }, [total])
  const goNext = useCallback(() => {
    if (!total) return
    setOpenIndex((i) => ((i ?? 0) + 1) % total)
  }, [total])

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        goPrev()
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        goNext()
      }
    },
    [goPrev, goNext],
  )

  const open = openIndex !== null
  const current = cards[active]

  /**
   * The Education Bureau's twelve core values and the disposition each one
   * is meant to build — the substance behind the twelve printed cards.
   */
  const valueTable = (t('products.offers.valuesIncubator.priorityCards.valueTable', {
    returnObjects: true,
  }) as {
    title: string
    colValue: string
    colCore: string
    rows: Array<{ value: string; core: string }>
  }) ?? { title: '', colValue: '', colCore: '', rows: [] }

  return (
    <section
      className="pv-section vi-deck-section py-section-y px-section-x bg-surface"
      aria-labelledby="pv-title"
    >
      <Container>
        <Reveal>
          <div className="text-center mb-8">
            <span className="section-title__tagline inline-block mb-3">
              {t('products.offers.valuesIncubator.priorityCards.tag')}
            </span>
            <h2
              id="pv-title"
              className="section-title__title text-3xl md:text-4xl text-ink"
            >
              {t('products.offers.valuesIncubator.priorityCards.title')}
            </h2>
            <p className="mt-4 text-lg text-ink-soft max-w-3xl mx-auto leading-relaxed">
              {t('products.offers.valuesIncubator.priorityCards.intro')}
            </p>
          </div>
        </Reveal>

        {/* ── The fan ─────────────────────────────────────────────
            Full width: the fan is a wide object (a hand of cards), so it
            gets the whole container rather than half a column. */}
        <Reveal>
          <div className="pv-fan-scroll">
            <div className="pv-fan">
              {cards.map((card, i) => {
                const { dx, dy, angle } = fanTransform(i, total)
                const transform = `translate(-50%, -50%) translateX(${dx}%) translateY(calc(50% + ${dy}%)) rotate(${angle}deg)`
                return (
                  <button
                    key={card.slug}
                    type="button"
                    onClick={(event) => openCard(i, event.currentTarget)}
                    className={hoverIndex === i ? 'pv-card is-lifted' : 'pv-card'}
                    style={
                      {
                        '--pv-transform': transform,
                        '--pv-tint': card.color,
                        /* The card's ink form: legible as a focus ring on
                           the cream surface, and the same colour the
                           legend chip for this card is filled with. */
                        '--pv-ink': inkFor(card.slug),
                        zIndex: i + 1,
                        // Stagger the lift so the fan does not snap.
                        transitionDelay: `${i * 15}ms`,
                      } as React.CSSProperties
                    }
                    aria-label={t('products.offers.valuesIncubator.priorityCards.cardAlt', {
                      name: card.name,
                      en: card.en,
                    })}
                  >
                    <img
                      src={card.image}
                      alt=""
                      width={1024}
                      height={1024}
                      loading="lazy"
                      decoding="async"
                    />
                  </button>
                )
              })}
            </div>
          </div>

          {/* ── The colour code ───────────────────────────────────
              Twelve numbered chips in deck order, each filled with the
              ink form of that card's colour. It doubles as the deck's
              index: hover a chip and its card lifts out of the fan,
              click it and that card opens. */}
          <div
            className="vi-legend"
            role="group"
            aria-label={t('products.offers.valuesIncubator.priorityCards.legendLabel')}
          >
            {cards.map((card, i) => (
              <button
                key={card.slug}
                type="button"
                className="vi-legend__chip"
                style={{ '--vi-chip': inkFor(card.slug) } as React.CSSProperties}
                onClick={(event) => openCard(i, event.currentTarget)}
                onMouseEnter={() => setHoverIndex(i)}
                onMouseLeave={() => setHoverIndex(null)}
                onFocus={() => setHoverIndex(i)}
                onBlur={() => setHoverIndex(null)}
                aria-current={openIndex === i ? 'true' : undefined}
                aria-label={t('products.offers.valuesIncubator.priorityCards.cardAlt', {
                  name: card.name,
                  en: card.en,
                })}
              >
                {card.no}
              </button>
            ))}
          </div>

          <p
            className="mt-4 text-center text-sm text-ink-muted"
            style={{ textAlign: 'center' }}
          >
            {t('products.offers.valuesIncubator.priorityCards.hint')}
          </p>
        </Reveal>

        {/* ── The material + the printed sheet ─────────────────── */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 mt-10 items-start">
          <Reveal>
            {/* The values behind the deck: the Education Bureau's twelve
                core values, each with the disposition it is meant to build.
                A table rather than cards — twelve rows of a value and its
                meaning read as a reference, not a gallery. */}
            <h3 className="text-lg font-bold text-ink mb-3">
              {valueTable.title}
            </h3>
            <div className="rounded-yada-lg border border-border overflow-hidden bg-surface-2">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-border bg-surface-3">
                    <th
                      scope="col"
                      className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-primary w-1/4"
                    >
                      {valueTable.colValue}
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-primary"
                    >
                      {valueTable.colCore}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {valueTable.rows.map((row) => (
                    <tr key={row.value} className="border-b border-border last:border-b-0">
                      <th
                        scope="row"
                        className="px-4 py-3 text-sm font-bold text-ink align-top"
                      >
                        {row.value}
                      </th>
                      <td className="px-4 py-3 text-sm text-ink-soft leading-relaxed">
                        {row.core}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <a
              href={PRIORITY_VALUES_PDF}
              download
              className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary-dark underline underline-offset-4"
            >
              {t('products.offers.valuesIncubator.priorityCards.downloadLabel')}
              <span aria-hidden="true">↓</span>
            </a>
          </Reveal>

          <Reveal delay={0.1}>
            <figure className="bg-surface-2 border border-border rounded-yada-xl overflow-hidden shadow-yada-lg max-w-md mx-auto lg:mx-0 lg:ml-auto">
              <img
                src={PRIORITY_VALUES_SHEET}
                alt={t('products.offers.valuesIncubator.priorityCards.sheetAlt')}
                width={2800}
                height={2800}
                loading="lazy"
                decoding="async"
                className="block w-full h-auto"
              />
              <figcaption className="text-sm text-ink-soft px-4 py-3 text-center border-t border-border bg-surface-3 leading-snug">
                {t('products.offers.valuesIncubator.priorityCards.sheetCaption')}
              </figcaption>
            </figure>
          </Reveal>
        </div>
      </Container>

      {/* ── Enlarged view ─────────────────────────────────────── */}
      {mounted && (
        <RadixDialog.Root
          open={open}
          onOpenChange={(next) => {
            if (next) return
            // Escape closes one layer at a time: the zoom view first, then
            // the lightbox. Closing both at once would throw away the
            // card the reader was looking at.
            if (zoomed) {
              setZoomed(false)
              return
            }
            setOpenIndex(null)
          }}
        >
          <AnimatePresence>
            {open && current && (
              <RadixDialog.Portal forceMount>
                <RadixDialog.Overlay asChild forceMount>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: reduced ? 0 : 0.15 }}
                    className="fixed inset-0 z-[60] bg-ink/95 backdrop-blur-sm"
                  />
                </RadixDialog.Overlay>

                <RadixDialog.Content
                  asChild
                  forceMount
                  onKeyDown={onKeyDown}
                  onCloseAutoFocus={(event) => {
                    // Radix restores focus to its own `Dialog.Trigger`, which
                    // does not exist here. Focus the fan card ourselves.
                    event.preventDefault()
                    triggerRef.current?.focus()
                  }}
                >
                  <motion.div
                    initial={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.97 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.97 }}
                    transition={{ duration: reduced ? 0 : 0.18, ease: [0.4, 0, 0.2, 1] }}
                    className="fixed inset-0 z-[61] flex flex-col items-center justify-center gap-6 px-4 py-8 focus:outline-none"
                  >
                    <RadixDialog.Title className="sr-only">
                      {t('products.offers.valuesIncubator.priorityCards.cardAlt', {
                        name: current.name,
                        en: current.en,
                      })}
                    </RadixDialog.Title>
                    <RadixDialog.Description className="sr-only">
                      {t('products.offers.valuesIncubator.priorityCards.counter', {
                        current: active + 1,
                        total,
                      })}
                    </RadixDialog.Description>

                    {zoomed ? (
                      /* The zoomed view replaces the lightbox body rather
                         than stacking on top of it: the enlarged card and
                         its text are noise once the artwork is at 1:1. */
                      <CardZoomViewer
                        src={current.image}
                        name={current.name}
                        onExit={() => setZoomed(false)}
                      />
                    ) : (
                      <>
                    <div className="flex items-center gap-3 sm:gap-6">
                      {total > 1 && (
                        <button
                          type="button"
                          onClick={goPrev}
                          aria-label={t('products.offers.valuesIncubator.priorityCards.prev')}
                          className="p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
                        >
                          <svg
                            aria-hidden="true"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="w-6 h-6"
                          >
                            <path d="m15 18-6-6 6-6" />
                          </svg>
                        </button>
                      )}

                      <button
                        type="button"
                        className="pv-card-large"
                        onClick={() => setZoomed(true)}
                        aria-label={t('products.offers.valuesIncubator.priorityCards.zoomOpen', {
                          name: current.name,
                        })}
                      >
                        <img
                          className="pv-card-large__img"
                          src={current.image}
                          alt=""
                          width={1024}
                          height={1024}
                          style={{
                            boxShadow:
                              '0 1px 0 rgba(255,255,255,.55) inset, ' +
                              '0 18px 40px -12px rgba(0,0,0,.55), ' +
                              `0 0 70px -14px ${current.color}`,
                          }}
                        />
                        <span className="pv-card-large__cta" aria-hidden="true">
                          <Icon icon="lucide:zoom-in" />
                          {t('products.offers.valuesIncubator.priorityCards.zoomOpenBadge')}
                        </span>
                      </button>

                      {total > 1 && (
                        <button
                          type="button"
                          onClick={goNext}
                          aria-label={t('products.offers.valuesIncubator.priorityCards.next')}
                          className="p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
                        >
                          <svg
                            aria-hidden="true"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="w-6 h-6"
                          >
                            <path d="m9 18 6-6-6-6" />
                          </svg>
                        </button>
                      )}
                    </div>

                    <div className="w-full max-w-xl text-white">
                      {/* The printed caps line — the cross-reference to the
                          card the reader is holding. It is omitted on the
                          English page, where that line IS the name. */}
                      {current.printedName && (
                        <p
                          className="text-xs tracking-[0.25em] text-accent uppercase mb-2"
                          style={{ textAlign: 'center' }}
                        >
                          {current.printedName}
                        </p>
                      )}
                      <p className="text-2xl font-bold mb-2" style={{ textAlign: 'center' }}>
                        {current.name}
                      </p>
                      <p
                        className="text-white/75 italic mb-4"
                        style={{ textAlign: 'center' }}
                      >
                        {current.slogan}
                      </p>
                      <p className="text-sm text-white/60 mb-2" style={{ textAlign: 'center' }}>
                        {t('products.offers.valuesIncubator.priorityCards.promptLabel')}
                      </p>
                      <p
                        className="text-base text-white/90 leading-relaxed"
                        style={{ textAlign: 'center' }}
                      >
                        {current.prompt}
                      </p>
                      <p
                        className="mt-5 text-sm text-white/50 tabular-nums"
                        style={{ textAlign: 'center' }}
                      >
                        {t('products.offers.valuesIncubator.priorityCards.counter', {
                          current: active + 1,
                          total,
                        })}
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => setOpenIndex(null)}
                      aria-label={t('products.offers.valuesIncubator.priorityCards.close')}
                      className="absolute right-3 top-3 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
                    >
                      <svg
                        aria-hidden="true"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="w-5 h-5"
                      >
                        <path d="M18 6 6 18" />
                        <path d="m6 6 12 12" />
                      </svg>
                    </button>
                      </>
                    )}
                  </motion.div>
                </RadixDialog.Content>
              </RadixDialog.Portal>
            )}
          </AnimatePresence>
        </RadixDialog.Root>
      )}
    </section>
  )
}
